import { FC, useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Stack, Chip, Avatar, Skeleton, Grid2 } from '@mui/material';
import { useParams } from 'react-router-dom';
import {
  Person,
  Assessment,
  CalendarToday,
  CorporateFare,
  CheckCircle,
  PendingActions,
  Stars,
  Phone as PhoneIcon,
  Email as EmailIcon,
} from '@mui/icons-material';

import { useJobseekerAdminFindOneQuery } from '@carcareers/store';
import { JobseekerAdminFindOneDto } from '@libs/dto';
import { SkeletonForm, DashboardPageContainer, InfoCard } from '@carcareers/components';
import { FormProvider } from '@carcareers/contexts';
import { useIsMobile } from '@carcareers/utils';
import { formatPhone } from '@libs/utils';

export const JobseekerView: FC = () => {
  const id = useParams().id;
  const isMobile = useIsMobile();

  const [jobseeker, setJobseeker] = useState<JobseekerAdminFindOneDto>();
  const { data, isLoading } = useJobseekerAdminFindOneQuery(id ?? '0', { skip: !id });

  useEffect(() => {
    if (!isLoading && data) {
      setJobseeker(data);
    }
  }, [isLoading, data]);

  const getInitials = (firstName?: string, lastName?: string) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const getCompletionColor = (percentage: number) => {
    if (percentage >= 80) return 'success.main';
    if (percentage >= 60) return 'warning.main';
    return 'error.main';
  };

  return (
    <DashboardPageContainer idReplacement={`${jobseeker?.user?.firstName} ${jobseeker?.user?.lastName}`}>
      <Stack spacing={3}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            backgroundColor: 'background.paper',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction={isMobile ? 'column' : 'row'} spacing={3} alignItems={isMobile ? 'flex-start' : 'center'}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: 'primary.main',
                  fontSize: '1.5rem',
                  fontWeight: 600,
                }}
              >
                {isLoading ? '' : getInitials(jobseeker?.user?.firstName, jobseeker?.user?.lastName)}
              </Avatar>
              <Stack spacing={1} sx={{ flex: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {isLoading ? (
                    <Skeleton variant="text" width={200} height={32} />
                  ) : (
                    `${jobseeker?.user?.firstName || ''} ${jobseeker?.user?.lastName || ''}`
                  )}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon color="primary" fontSize="small" />
                    <Typography variant="body1" color="text.secondary">
                      {jobseeker?.user?.email || ''}
                    </Typography>
                  </Box>
                  {jobseeker?.user?.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon color="primary" fontSize="small" />
                      <Typography variant="body1" color="text.secondary">
                        {formatPhone(jobseeker.user.phone)}
                      </Typography>
                    </Box>
                  )}
                </Box>
                {jobseeker?.mostRecentJobTitle && (
                  <Typography variant="body2" color="text.secondary">
                    {jobseeker.mostRecentJobTitle} {jobseeker.mostRecentCompany && `at ${jobseeker.mostRecentCompany}`}
                  </Typography>
                )}
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<Person />}
                    label={`User ID: ${jobseeker?.user?.id || '...'}`}
                    variant="outlined"
                    sx={{ px: 1 }}
                  />
                  <Chip
                    icon={<CorporateFare />}
                    label={`Jobseeker ID: ${jobseeker?.id || '...'}`}
                    variant="outlined"
                    sx={{ px: 1 }}
                  />
                  <Chip
                    icon={<CalendarToday />}
                    label={
                      jobseeker?.createdAt
                        ? `Joined ${new Date(jobseeker.createdAt).toLocaleDateString()}`
                        : 'Joined ...'
                    }
                    variant="outlined"
                    sx={{ px: 1 }}
                  />
                  {jobseeker?.profileCompleteness !== undefined && (
                    <Chip
                      icon={<Stars />}
                      label={`Profile: ${jobseeker.profileCompleteness}%`}
                      variant="outlined"
                      sx={{
                        px: 1,
                        color: getCompletionColor(jobseeker.profileCompleteness),
                        borderColor: getCompletionColor(jobseeker.profileCompleteness),
                      }}
                    />
                  )}
                </Stack>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
        <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
          <Card
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 3,
              backgroundColor: 'background.paper',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  backgroundColor: 'primary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <Assessment sx={{ color: 'primary.contrastText', fontSize: 28 }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                {isLoading ? <Skeleton variant="text" width={40} height={32} /> : jobseeker?.totalApplications || 0}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Total Applications
              </Typography>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 3,
              backgroundColor: 'background.paper',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  backgroundColor: 'success.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <CheckCircle sx={{ color: 'success.contrastText', fontSize: 28 }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main', mb: 1 }}>
                {isLoading ? <Skeleton variant="text" width={40} height={32} /> : jobseeker?.activeApplications || 0}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Active Applications
              </Typography>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 3,
              backgroundColor: 'background.paper',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  backgroundColor: 'warning.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <PendingActions sx={{ color: 'primary.contrastText', fontSize: 28 }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'warning.main', mb: 1 }}>
                {isLoading ? <Skeleton variant="text" width={40} height={32} /> : jobseeker?.withdrawnApplications || 0}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Withdrawn Applications
              </Typography>
            </CardContent>
          </Card>
        </Stack>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <InfoCard label="Work Experience" value={jobseeker?.experiencesCount || 0} loading={isLoading} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <InfoCard label="Education" value={jobseeker?.educationsCount || 0} loading={isLoading} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <InfoCard label="Professional References" value={jobseeker?.referencesCount || 0} loading={isLoading} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <InfoCard label="Certifications" value={jobseeker?.certificatesCount || 0} loading={isLoading} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <InfoCard label="Associations" value={jobseeker?.associationsCount || 0} loading={isLoading} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <InfoCard label="Video Responses" value={jobseeker?.videosCount || 0} loading={isLoading} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <InfoCard label="Saved Jobs" value={jobseeker?.savedJobsCount || 0} loading={isLoading} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <InfoCard label="Has Resume" value={jobseeker?.hasResume ? 'Yes' : 'No'} loading={isLoading} />
          </Grid2>
        </Grid2>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <InfoCard label="Location" value={jobseeker?.location || 'Not specified'} loading={isLoading} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <InfoCard label="Zip Code" value={jobseeker?.zipCode || 'Not specified'} loading={isLoading} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <InfoCard
              label="Last Application"
              value={
                jobseeker?.lastApplicationDate ? new Date(jobseeker.lastApplicationDate).toLocaleDateString() : 'Never'
              }
              loading={isLoading}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <InfoCard
              label="Profile Completeness"
              value={
                jobseeker?.profileCompleteness !== undefined ? `${jobseeker.profileCompleteness}%` : 'Calculating...'
              }
              loading={isLoading}
            />
          </Grid2>
        </Grid2>
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            backgroundColor: 'background.paper',
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person sx={{ color: 'primary.main' }} />
                Jobseeker Details
              </Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              <FormProvider initialData={jobseeker}>
                {isLoading ? (
                  <SkeletonForm />
                ) : (
                  <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    Jobseeker update form coming soon...
                  </Typography>
                )}
              </FormProvider>
            </Box>
          </CardContent>
        </Card>
      </Stack>
    </DashboardPageContainer>
  );
};

export default JobseekerView;
